const WS_RETRY_ATTEMPTS = 10;
const WS_RETRY_BASE_MS = 1_000;
const WS_RETRY_MAX_MS = 30_000;
const WS_KEEPALIVE_MS = 10_000;

type WsCloseListener = () => void;

type WsConnectionEvents = {
  onClose: (listener: WsCloseListener) => () => void;
  emitClose: () => void;
};

const closeListeners = new Set<WsCloseListener>();

/**
 * Module-level pub/sub for WebSocket connection lifecycle events.
 * Used by useConversationEvents to immediately clear streaming UI
 * when the WS connection drops, rather than waiting for stale-turn timeout.
 */
export const wsConnectionEvents: WsConnectionEvents = {
  onClose: (listener: WsCloseListener): (() => void) => {
    closeListeners.add(listener);
    return () => {
      closeListeners.delete(listener);
    };
  },
  emitClose: () => {
    for (const listener of closeListeners) {
      try {
        listener();
      } catch (error) {
        console.error(
          "[wsConnectionEvents] listener threw during emitClose",
          error,
        );
      }
    }
  },
};

/**
 * Shared graphql-ws reconnection options for both web and native clients.
 * Provides 10 retries with exponential backoff, keepAlive pings, and
 * lifecycle logging with wsConnectionEvents integration.
 */
export const wsReconnectOptions = {
  retryAttempts: WS_RETRY_ATTEMPTS,
  retryWait: async (retries: number) => {
    const delay = Math.min(WS_RETRY_BASE_MS * 2 ** retries, WS_RETRY_MAX_MS);
    await new Promise<void>(resolve => setTimeout(resolve, delay));
  },
  keepAlive: WS_KEEPALIVE_MS,
  on: {
    connected: () => console.debug("[ws] connected"),
    closed: () => {
      console.warn("[ws] closed");
      wsConnectionEvents.emitClose();
    },
    error: (error: unknown) => console.error("[ws] error", error),
  },
} as const;
